import { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import cloneService from '../../services/cloneService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';

const PAGE_SIZE = 20;

const CloneProductsIndex = () => {
  const { isRtl } = useLocalization();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [cloningId, setCloningId] = useState(null);
  const [draft, setDraft] = useState(null); // editable preview of the product being cloned
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await cloneService.getSourceProducts({ search, page, size: PAGE_SIZE });
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('Error loading source products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openClone = async (product) => {
    if (product.existsInTarget || isPreviewLoading) return;
    setIsPreviewLoading(true);
    try {
      const data = await cloneService.getSourceProduct(product.id);
      setDraft({ ...data, variants: data.variants.map((v) => ({ ...v, include: true })) });
    } catch (error) {
      console.error('Error loading product preview:', error);
      alert(isRtl ? 'تعذر تحميل المنتج' : 'Failed to load product');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setVariant = (id, field, value) =>
    setDraft((d) => ({
      ...d,
      variants: d.variants.map((v) => (v.sourceVariantId === id ? { ...v, [field]: value } : v)),
    }));

  const handleClone = async () => {
    if (!draft || cloningId) return;
    const chosen = draft.variants.filter((v) => v.include);
    if (chosen.length === 0) {
      alert(isRtl ? 'اختر حجماً واحداً على الأقل' : 'Select at least one variant');
      return;
    }
    const id = draft.id;
    const markExists = () =>
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, existsInTarget: true } : p)));
    setCloningId(id);
    try {
      await cloneService.cloneProduct(id, {
        nameEn: draft.nameEn,
        nameAr: draft.nameAr,
        descriptionEn: draft.descriptionEn,
        descriptionAr: draft.descriptionAr,
        isPublished: draft.isPublished,
        variants: chosen.map((v) => ({
          sourceVariantId: v.sourceVariantId,
          sizeLabelEn: v.sizeLabelEn,
          sizeLabelAr: v.sizeLabelAr,
          price: Number(v.price) || 0,
          inStock: v.inStock,
          sku: v.sku || null,
        })),
      });
      markExists();
      setDraft(null);
    } catch (error) {
      const status = error.response?.status;
      if (status === 409) {
        alert(isRtl ? 'هذا المنتج موجود بالفعل' : 'This product already exists');
      } else {
        console.error('Error cloning product:', error);
        alert(isRtl ? 'تعذر استنساخ المنتج' : 'Failed to clone product');
      }
    } finally {
      setCloningId(null);
    }
  };

  return (
    <div className="clone-products-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'استنساخ من المتجر الرئيسي' : 'Clone from Main Store'}</h1>
            <p className="page-description">
              {isRtl
                ? 'تصفح منتجات المتجر الرئيسي (rimalalkhaleej.com) وانسخها إلى هذا المتجر.'
                : "Browse products from the main store (rimalalkhaleej.com) and copy them into this store."}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <form className="search-box" onSubmit={submitSearch}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن منتج...' : 'Search products...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </form>
          <div className="card-header-info hidden-mobile">
            <span className="text-muted">
              {isRtl ? `${totalCount} منتج` : `${totalCount} products`}
            </span>
          </div>
        </div>

        <div className="card-body p-0">
          {isLoading ? (
            <Loading text={isRtl ? 'جاري التحميل...' : 'Loading products...'} />
          ) : items.length === 0 ? (
            <div className="empty-state">
              <p className="empty-description">{isRtl ? 'لا توجد منتجات' : 'No products found'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{isRtl ? 'المنتج' : 'Product'}</th>
                    <th>{isRtl ? 'الفئة' : 'Category'}</th>
                    <th>{isRtl ? 'العلامة التجارية' : 'Brand'}</th>
                    <th>{isRtl ? 'الحالة' : 'Status'}</th>
                    <th>{isRtl ? 'الإجراءات' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((product) => {
                    const imageUrl = getProductImageUrl(product.primaryImageUrl);
                    return (
                      <tr key={product.id}>
                        <td>
                          <div className="product-cell">
                            <div className="product-thumb-wrap">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={product.nameEn}
                                  className="product-thumb"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : null}
                            </div>
                            <div className="product-info">
                              <div className="product-name">{isRtl ? product.nameAr : product.nameEn}</div>
                              {isRtl && product.nameEn ? <div className="product-name-sub">{product.nameEn}</div> : null}
                            </div>
                          </div>
                        </td>
                        <td><span className="text-secondary">{product.categoryNameEn || '-'}</span></td>
                        <td><span className="text-secondary">{isRtl ? product.brandNameAr : product.brandNameEn}</span></td>
                        <td>
                          <span className={`badge ${product.existsInTarget ? 'badge-success' : 'badge-warning'}`}>
                            {product.existsInTarget
                              ? (isRtl ? 'موجود' : 'Exists')
                              : (isRtl ? 'غير موجود' : 'Not cloned')}
                          </span>
                        </td>
                        <td>
                          <Button
                            size="small"
                            variant={product.existsInTarget ? 'secondary' : 'primary'}
                            disabled={product.existsInTarget || isPreviewLoading}
                            onClick={() => openClone(product)}
                          >
                            {product.existsInTarget
                              ? (isRtl ? 'موجود بالفعل' : 'Already exists')
                              : (isRtl ? 'استنساخ' : 'Clone')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 ? (
          <div className="clone-pagination">
            <Button
              size="small"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {isRtl ? 'السابق' : 'Previous'}
            </Button>
            <span className="text-muted">
              {isRtl ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}
            </span>
            <Button
              size="small"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {isRtl ? 'التالي' : 'Next'}
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={!!draft}
        onClose={() => !cloningId && setDraft(null)}
        title={isRtl ? 'مراجعة وتعديل قبل الاستنساخ' : 'Review & edit before cloning'}
        size="large"
        footer={
          <>
            <Button variant="secondary" disabled={!!cloningId} onClick={() => setDraft(null)}>
              {isRtl ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button variant="primary" disabled={!!cloningId} onClick={handleClone}>
              {cloningId ? (isRtl ? 'جاري الاستنساخ...' : 'Cloning...') : (isRtl ? 'استنساخ' : 'Clone')}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="clone-form">
            <div className="clone-grid">
              <label>
                <span>{isRtl ? 'الاسم (EN)' : 'Name (EN)'}</span>
                <input className="form-control" value={draft.nameEn} onChange={(e) => setField('nameEn', e.target.value)} />
              </label>
              <label>
                <span>{isRtl ? 'الاسم (AR)' : 'Name (AR)'}</span>
                <input className="form-control" dir="rtl" value={draft.nameAr} onChange={(e) => setField('nameAr', e.target.value)} />
              </label>
              <label>
                <span>{isRtl ? 'الوصف (EN)' : 'Description (EN)'}</span>
                <textarea className="form-control" rows={3} value={draft.descriptionEn || ''} onChange={(e) => setField('descriptionEn', e.target.value)} />
              </label>
              <label>
                <span>{isRtl ? 'الوصف (AR)' : 'Description (AR)'}</span>
                <textarea className="form-control" dir="rtl" rows={3} value={draft.descriptionAr || ''} onChange={(e) => setField('descriptionAr', e.target.value)} />
              </label>
            </div>

            <label className="clone-check">
              <input type="checkbox" checked={draft.isPublished} onChange={(e) => setField('isPublished', e.target.checked)} />
              <span>{isRtl ? 'ظاهر في المتجر' : 'Published in store'}</span>
            </label>

            <h4 className="clone-section-title">
              {isRtl ? `الأحجام والأسعار (${draft.currencyCode})` : `Variants & prices (${draft.currencyCode})`}
            </h4>
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>{isRtl ? 'الحجم' : 'Size'}</th>
                  <th>{isRtl ? 'السعر' : 'Price'}</th>
                  <th>SKU</th>
                  <th>{isRtl ? 'متوفر' : 'In stock'}</th>
                </tr>
              </thead>
              <tbody>
                {draft.variants.map((v) => (
                  <tr key={v.sourceVariantId} style={{ opacity: v.include ? 1 : 0.5 }}>
                    <td><input type="checkbox" checked={v.include} onChange={(e) => setVariant(v.sourceVariantId, 'include', e.target.checked)} /></td>
                    <td>{isRtl ? v.sizeLabelAr : v.sizeLabelEn}</td>
                    <td>
                      <input type="number" min="0" step="any" className="form-control" style={{ width: 110 }}
                        value={v.price} disabled={!v.include}
                        onChange={(e) => setVariant(v.sourceVariantId, 'price', e.target.value)} />
                    </td>
                    <td>
                      <input className="form-control" style={{ width: 130 }} value={v.sku || ''} disabled={!v.include}
                        onChange={(e) => setVariant(v.sourceVariantId, 'sku', e.target.value)} />
                    </td>
                    <td><input type="checkbox" checked={v.inStock} disabled={!v.include} onChange={(e) => setVariant(v.sourceVariantId, 'inStock', e.target.checked)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Modal>

      <style>{`
        .clone-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sp-4); margin-bottom: var(--sp-4); }
        .clone-grid label { display: flex; flex-direction: column; gap: var(--sp-1); font-size: var(--fs-sm); }
        .clone-check { display: flex; align-items: center; gap: var(--sp-2); margin-bottom: var(--sp-4); }
        .clone-section-title { margin: var(--sp-4) 0 var(--sp-2); }
        @media (max-width: 640px) { .clone-grid { grid-template-columns: 1fr; } }
        .product-cell {
          display: flex;
          align-items: center;
          gap: var(--sp-3);
        }
        .product-thumb-wrap {
          position: relative;
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          border-radius: var(--r-md);
          overflow: hidden;
          background: var(--neutral-100);
        }
        .product-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .product-info {
          min-width: 0;
        }
        .product-name {
          font-weight: var(--fw-semibold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .product-name-sub {
          font-size: var(--fs-xs);
          color: var(--text-muted);
        }
        .card-header-info {
          font-size: var(--fs-sm);
        }
        .clone-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--sp-4);
          padding: var(--sp-4);
          border-top: 1px solid var(--border-light);
        }
      `}</style>
    </div>
  );
};

export default CloneProductsIndex;
